import { FC, HTMLAttributes } from "react";
import { useStyles } from "./ProfileInfo.style";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { Icon } from "../../core/Icon/Icon";
import Button from "../../core/Button/Button";
import { TabsList } from "../TabsList";
import { ILevel } from "../../../types/user";
import Avatar from "../../core/Avatar/Avatar";
import { StaticImage } from "../StaticImage";
import { useNavigate, useParams } from "react-router-dom";
import { PageRoutes } from "../../../constants/historyGaming";
import { useAppSelector } from "../../hooks";
import { ProfileInfoLoading } from "../Loading/ProfileInfoLoading/ProfileInfoLoading";
import { FollowStatus, User } from "@onmo/graphql/graphql";
import { useTranslation } from "react-i18next";
import GZIcon from "../../../assets/rankGem/webp/gz_coin_1x.webp";
import GZIcon2x from "../../../assets/rankGem/webp/icon2x/gz_coin_2x.webp";
import GZIcon3x from "../../../assets/rankGem/webp/icon3x/gz_coin_3x.webp";

import {
  useFollowUser,
  useUnfollowUser,
} from "../../../hooks/resolvers/friend/friend";
import { ProgressBar } from "../ProgressBar";
import Badge from "../../core/Badge/Badge";

interface ITabsListProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  listTabs: string[];
  userRank?: ILevel & { nextLevel?: ILevel };
  profile?: User;
  gzTokens?: number;
}

export const ProfileInfo: FC<
  HTMLAttributes<HTMLDivElement> & ITabsListProps
> = ({
  listTabs,
  activeTab,
  setActiveTab,
  profile,
  userRank,
  gzTokens = 0,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId: userIdParam }: { userId?: string } = useParams();
  const followStatus = !userIdParam ? undefined : profile?.followStatus;
  const { theme } = useAppSelector((state) => state.theme);

  const followUser = useFollowUser(profile as User);
  const unfollowUser = useUnfollowUser(profile as User);

  const getIconByFriendStatus = () => {
    switch (followStatus) {
      case FollowStatus.FollowedBy:
        return "icon-profile-tick";
      case FollowStatus.FollowingAndFollowedBy:
        return "icon-profile-2user";
      case undefined:
        return "icon-bold-edit-2";
      default:
        return undefined;
    }
  };
  const onClickFriendButton = (friendTab: "Following" | "Followers") => {
    if (userIdParam) return;
    navigate({
      pathname: PageRoutes.Friends,
      search: `friendTab=${friendTab}`,
    });
  };

  const onClickAvatar = () => {
    if (userIdParam) return;
    navigate(`${PageRoutes.Level}`);
  };

  const onClickProgress = () => {
    if (userIdParam) return;
    navigate(`${PageRoutes.Level}`);
  };

  const onEditProfile = () => {
    if (userIdParam) return;
    navigate(`${PageRoutes.EditProfile}`);
  };

  if (!profile) return <ProfileInfoLoading />;

  return (
    <div className={classes.root}>
      <StaticImage src={profile?.avatar} className={classes.avatarBackground} />
      <div className={classes.content}>
        <div className={classes.profileInfo}>
          <Avatar
            userRank={userRank}
            url={profile?.avatar}
            size="ml"
            onClick={onClickAvatar}
            data-testid="avatar-user"
            hideProgress={true}
          />
          <div className={classes.info}>
            <div className={classes.infoItem}>
              <Typography
                variant={TypographyVariant.HeadingH4Bold}
                className={classes.username}
                data-testid="username"
                lines={2}
              >
                {profile?.username}
              </Typography>
              {getIconByFriendStatus() && (
                <Icon
                  name={getIconByFriendStatus()}
                  size={24}
                  className={classes.editIcon}
                  onClick={onEditProfile}
                  data-testid="edit-icon"
                />
              )}
            </div>
          </div>
        </div>

        <div className={classes.levelContainer} onClick={onClickProgress}>
          {userRank && (
            <div className={classes.progressContainer}>
              <ProgressBar
                color={userRank.color || ""}
                progress={userRank.levelPercent}
                size="medium"
                data-testid="gem-progress-bar"
              />
              {userRank.nextLevel && (
                <Badge
                  size={"xxl-48"}
                  type={userRank.nextLevel?.id}
                  id={"badge"}
                  data-testid="rank-badge"
                />
              )}
            </div>
          )}
          {userRank && userRank.nextLevel && (
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              className={classes.gemNeeded}
            >
              <b>{userRank.nextLevel.xp - gzTokens}</b>
              <StaticImage
                src={GZIcon}
                src2x={GZIcon2x}
                src3x={GZIcon3x}
                width={16}
              />
              to {userRank.nextLevel.name}
            </Typography>
          )}
        </div>
        <div className={classes.buttonsGroup}>
          <Button
            size="smaller"
            color={theme.colors.Tertiary}
            fullWidth
            onClick={() => onClickFriendButton("Followers")}
            disabledRipple={!!userIdParam}
            data-testid="flowers-button"
          >
            {profile?.followers.total} {t("common_Followers")}
          </Button>
          <Button
            size="smaller"
            color={theme.colors.Tertiary}
            fullWidth
            onClick={() => onClickFriendButton("Following")}
            disabledRipple={!!userIdParam}
            data-testid="following-button"
          >
            {profile?.following.total} {t("common_Following")}
          </Button>
        </div>
        {(followStatus === FollowStatus.NoFollow ||
          followStatus === FollowStatus.Following) && (
          <Button
            afterIcon="icon-linear-add"
            className={classes.button}
            onClick={followUser}
          >
            {t("common_Follow")}
          </Button>
        )}
        {followStatus === FollowStatus.Following && (
          <Typography variant={TypographyVariant.BodyB5Medium}>
            {t("common_user is following you, you could follow back", {
              username: profile?.username,
            })}
          </Typography>
        )}
        {(followStatus === FollowStatus.FollowedBy ||
          followStatus === FollowStatus.FollowingAndFollowedBy) && (
          <Button
            afterIcon="icon-profile-remove"
            variant="outlined"
            className={classes.button}
            onClick={unfollowUser}
          >
            {t("common_Unfollow")}
          </Button>
        )}
        <span className={classes.line} />
        {!followStatus && (
          <TabsList
            listTabs={listTabs}
            activeTab={activeTab}
            onChangeTab={setActiveTab}
          />
        )}
      </div>
    </div>
  );
};
